import React, { useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { SearchFilterValue } from "../@types/SearchFilterValue"
import { SearchResultItem } from "../@types/SearchResultItem"

export interface SiteSearchFilterContextValue {
	activeFilterIds?: string[]
	activeItems: SearchResultItem[]
	updateActiveFilterIds: (name: string) => void
}

export const SiteSearchFilterContext =
	React.createContext<SiteSearchFilterContextValue>({
		activeFilterIds: [],
		activeItems: [],
		updateActiveFilterIds: (name: string) => null,
	})

interface SearchFilterContextProviderProps {
	searchFilter: SearchFilterValue[]
	items: SearchResultItem[]
	children?: React.ReactNode
}

export const SiteSearchFilterContextProvider: React.FC<SearchFilterContextProviderProps> =
	({ searchFilter, items, children }) => {
		const getActiveIdsFromSearchParams = (
			name: string,
			validOptions: string[]
		) => {
			const [searchParams] = useSearchParams()
			const value = searchParams.get(name)
			if (!value) return []

			const values = value.split(",")

			// Filter out invalid options
			return values.filter((v) => validOptions.includes(v))
		}

		const [activeFilterIds, setActiveFilterIds] = React.useState(
			getActiveIdsFromSearchParams(
				"filter",
				searchFilter.map((filter) => filter.id)
			)
		)

		const updateActiveFilterIds = (id: string) => {
			if (activeFilterIds.includes(id)) {
				// Remove id
				setActiveFilterIds(
					activeFilterIds.filter((activeId) => activeId !== id)
				)
			} else {
				// Add id
				setActiveFilterIds([...activeFilterIds, id])
			}
		}

		const [activeItems, setActiveItems] =
			React.useState<SearchResultItem[]>(items)

		React.useEffect(() => {
			let filteredItems = items

			if (activeFilterIds.length > 0) {
				filteredItems = filteredItems.filter((item) =>
					activeFilterIds.includes(item.typeId)
				)
			}
			setActiveItems(filteredItems)
		}, [activeFilterIds])

		useEffect(() => {
			const url = new URL(window.location.href)
			if (!activeFilterIds.length) {
				url.searchParams.delete("filter")
			} else {
				url.searchParams.set("filter", activeFilterIds.join(","))
			}

			window.history.replaceState({}, "", url)
		}, [activeFilterIds])

		return (
			<SiteSearchFilterContext.Provider
				value={{
					activeFilterIds,
					activeItems,
					updateActiveFilterIds,
				}}
			>
				{children}
			</SiteSearchFilterContext.Provider>
		)
	}
